import { getActivationGate, getUserProfile } from "../Services/MyProfile";

/**
 * Confirm activation against the server and persist the answer.
 *
 * **Why a shared helper.** Activation finishes server-side, inside
 * `ActivationService.complete()`, on a tool call the member never sees. Nothing
 * is pushed to the client: `activation.completed` goes onto the platform event
 * bus for Explore personalization, not to a device. So the only way a client
 * learns is by asking, and three places ask — the conversation screens, the
 * activation-gated routes, and the tab bar. They were asking the same question
 * and doing different things with the answer: two refreshed the cached profile,
 * the tab bar only flipped its own state.
 *
 * That difference is the whole bug. The tab bar is the one that runs on *every*
 * navigation, so it is where completion is almost always noticed first — and
 * because it never wrote, `localStorage.profile` kept saying `activation_pending`
 * indefinitely. The member's tabs appeared, so it looked finished, while every
 * other consumer of the cached profile still read them as mid-activation and
 * every navigation paid for another gate call that changed nothing.
 *
 * @returns {Promise<boolean|null>} whether activation is still pending, or
 *   `null` when the gate could not be reached — no new information, so callers
 *   must keep trusting their cache rather than guessing.
 */
export const confirmActivationState = async () => {
  const gate = await getActivationGate();
  if (!gate) return null;

  const pending = Boolean(gate.activation_pending);
  if (pending) return true;

  // Completed. The cached profile is the app's source of truth for onboarding
  // state, so writing it here is what actually ends the member's activation as
  // far as the rest of the UI is concerned.
  try {
    const profile = await getUserProfile();
    if (profile) {
      localStorage.setItem("profile", JSON.stringify(profile));
      // Same-tab writes do not raise `storage`, so tell listeners directly.
      window.dispatchEvent(new Event("profile-updated"));
    }
  } catch (e) {
    // The gate is authoritative and said complete; a failed profile refetch
    // costs the cache an update, not the member their access. The next caller
    // retries, because the cache still reads pending.
    console.error("Activation complete, but the profile refetch failed", e);
  }
  return false;
};

export default confirmActivationState;
